import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { SNTicketEventAction } from '@nsc/servicenow/types.js';
import {
  PROBLEM_REPORTS_ENABLED,
  SmartTeamsCustomer,
  type ProblemReportKey,
} from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';

/**
 * Which opt-in "Report a problem" button a ticket-event action belongs to, for per-customer
 * enforcement. Workflow/parts/ETA actions are deliberately absent — they can't be configured per
 * customer. `cancellation_requested` is also absent on purpose: cancellation is always available
 * to the FE and never gated.
 */
const ACTION_TO_PROBLEM_REPORT: Partial<Record<SNTicketEventAction, ProblemReportKey>> = {
  customer_requested_reschedule: 'cad',
  unsuccessful_visit: 'cus',
  attempted_contact: 'cna',
  access_not_granted: 'cra',
  other_problem: 'tpm',
};

/**
 * Buttons the customer (owning the given SN client sys_id) has opted into. Empty when the client
 * isn't linked to a SmartTeamsCustomer record (or hasn't enabled any) — opt-in, so nothing is
 * available until an admin explicitly turns it on.
 */
export async function getEnabledProblemReports(clientSysId: string | null | undefined): Promise<string[]> {
  // Feature off: every opt-in problem report is disabled product-wide (see PROBLEM_REPORTS_ENABLED).
  // Returning empty here both hides the FE buttons and makes assertProblemReportEnabled reject them,
  // regardless of any stored per-customer data.
  if (!PROBLEM_REPORTS_ENABLED) return [];
  if (!clientSysId) return [];
  const col = await getRawCollection<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName);
  const customer = await col.findOne(
    { serviceNowClientId: clientSysId },
    { projection: { enabledProblemReports: 1 } },
  );
  return customer?.enabledProblemReports ?? [];
}

/**
 * Server-side counterpart of showing the button: reject a problem-report action the customer
 * hasn't opted into, so the UI gate can't be bypassed. No-op for actions that aren't
 * customer-configurable (workflow/parts/ETA, and always-on cancellation).
 */
export async function assertProblemReportEnabled(
  action: SNTicketEventAction,
  clientSysId: string | null | undefined,
): Promise<void> {
  const key = ACTION_TO_PROBLEM_REPORT[action];
  if (!key) return;
  const enabled = await getEnabledProblemReports(clientSysId);
  if (!enabled.includes(key)) {
    throw new Error('This problem report is not enabled for this customer');
  }
}
